import type { ThemeConfig } from "@/types/theme";

export const tailwindLime: ThemeConfig = {
  classes: {
    // Color classes
    primary: "text-lime-700 dark:text-lime-400",
    secondary: "text-lime-900 dark:text-lime-500",
    accent: "text-emerald-600 dark:text-emerald-400",

    // Background classes
    backgroundPrimary: "bg-white dark:bg-gray-950",
    backgroundSecondary: "bg-gray-50 dark:bg-gray-900",
    backgroundSurface: "bg-gray-50 dark:bg-gray-800",
    backgroundTertiary: "bg-lime-50/60 dark:bg-gray-800",
    backgroundAccent: "bg-lime-600 dark:bg-lime-700",
    backgroundPrimaryHover: "hover:bg-gray-50 dark:hover:bg-gray-900",
    decorativeBackground:
      "bg-gradient-to-br from-lime-50 to-emerald-50 dark:from-gray-900 dark:to-gray-950",

    // Shared alternates (same across palettes)
    backgroundSoft: "bg-gray-50 dark:bg-gray-900",
    backgroundLighter: "bg-white dark:bg-gray-900",

    backgroundPattern: "bg-lime-100 dark:bg-gray-800",
    backgroundPrimaryColor: "bg-lime-600 dark:bg-lime-500",
    backgroundSecondaryColor: "bg-lime-800 dark:bg-lime-700",
    backgroundAccentColor: "bg-emerald-500 dark:bg-emerald-400",
    backgroundPrimarySubtle: "bg-lime-50 dark:bg-lime-950",

    // Card backgrounds
    backgroundCard: "bg-white dark:bg-gray-950",
    backgroundElevated: "bg-white dark:bg-gray-800",
    backgroundHighlighted:
      "bg-gradient-to-br from-white to-lime-50 dark:from-gray-800 dark:to-gray-900",

    // Text classes
    textPrimary: "text-gray-800 dark:text-gray-100",
    textSecondary: "text-gray-600 dark:text-gray-300",
    textMuted: "text-gray-500 dark:text-gray-400",
    textInverse: "text-white dark:text-gray-900",
    textAccent: "text-lime-700 dark:text-lime-400",
    textOnAccent: "text-white",

    // Text on dark backgrounds
    textPrimaryOnDark: "text-gray-100",
    textSecondaryOnDark: "text-gray-300",
    textMutedOnDark: "text-gray-400",

    // Icon classes
    iconText: "text-lime-600 dark:text-lime-400",
    iconBackgroundFilled: "bg-lime-100 dark:bg-lime-950",
    iconBackgroundGradient: "bg-gradient-to-br from-lime-500 to-emerald-500",
    iconBackgroundDefault: "bg-lime-50 dark:bg-lime-950/50",
    iconBorder: "border-lime-200 dark:border-lime-800",

    // Border classes
    borderDefault: "border-lime-200 dark:border-gray-700",
    borderLight: "border-lime-100 dark:border-gray-800",
    borderStrong: "border-lime-300 dark:border-gray-600",
    borderPrimary: "border-lime-700 dark:border-lime-500",
    borderSecondary: "border-lime-900 dark:border-lime-700",
    borderBottom: "border-b border-lime-200 dark:border-gray-700",
    borderAccent: "border-lime-600 dark:border-lime-400",

    // Badge classes
    badgeBackground: "bg-lime-100 dark:bg-lime-950",
    badgeText: "text-lime-700 dark:text-lime-300",

    // Shadow classes
    shadowPrimary: "shadow-lime-500/20",
    shadowDefault: "shadow-sm",
    shadowHover: "hover:shadow-lg hover:shadow-lime-500/10",
    shadowSmall: "shadow-sm",
    shadowMedium: "shadow-md",
    shadowLarge: "shadow-lg",
    shadowXLarge: "shadow-xl",
    shadowGlow: "shadow-[0_0_30px_rgba(132,204,22,0.30)]", // lime-500 glow
    shadowInset: "shadow-inner",

    // Interactive states
    hoverBackground: "hover:bg-lime-50 dark:hover:bg-gray-800",
    activeBackground: "active:bg-lime-100 dark:active:bg-gray-700",
    accentPrimary:
      "bg-lime-600 hover:bg-lime-700 dark:bg-lime-700 dark:hover:bg-lime-600",

    // Creative Bold Hero classes
    backgroundBold: "bg-lime-400 dark:bg-lime-600",
    textEmphasis: "text-violet-600 dark:text-violet-400",

    // Gradient classes
    gradientPrimary: "bg-gradient-to-r from-lime-600 to-lime-700",
    gradientSecondary: "bg-gradient-to-br from-lime-700 to-lime-900",
    gradientAccent: "bg-gradient-to-r from-lime-500 to-emerald-500",
    gradientSubtle:
      "bg-gradient-to-br from-lime-50 to-emerald-50 dark:from-gray-900 dark:to-gray-950",
    gradientVibrant:
      "bg-gradient-to-r from-lime-600 via-emerald-500 to-lime-600",
    gradientHero: "bg-gradient-to-br from-lime-600 via-emerald-500 to-lime-700",

    // Border radius classes
    roundedSmall: "rounded",
    roundedMedium: "rounded-md",
    roundedLarge: "rounded-lg",
    roundedXLarge: "rounded-xl",
    roundedFull: "rounded-full",

    // Opacity classes
    opacityDisabled: "opacity-50",
    opacityHover: "opacity-80",
    opacityBackdrop: "opacity-60",
    opacitySubtle: "opacity-30",

    // Transition classes
    transitionFast: "transition-all duration-150",
    transitionNormal: "transition-all duration-300",
    transitionSlow: "transition-all duration-500",

    // Z-index classes
    zDropdown: "z-10",
    zModal: "z-50",
    zOverlay: "z-40",
    zTooltip: "z-[60]",
    zFixed: "z-30",

    // Status colors (kept consistent across palettes)
    error: "text-red-600 dark:text-red-400",
    success: "text-green-600 dark:text-green-400",
    warning: "text-amber-600 dark:text-amber-400",
    info: "text-blue-600 dark:text-blue-400",

    errorBg: "bg-red-100 dark:bg-red-950",
    successBg: "bg-green-100 dark:bg-green-950",
    warningBg: "bg-amber-100 dark:bg-amber-950",
    infoBg: "bg-blue-100 dark:bg-blue-950",
  },
};
